import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiAlertCircle, FiFileText, FiChevronDown, FiCheck, FiPlay, FiArrowRight, FiMessageCircle } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { SetUser } from '../../../actions/SetUser';
import { toast } from '../Toast';
import { SUPPORT_EMAIL } from './helpContent';
import { findGuide, findVideo, guideHref, videoHref, topicHref, HELP_UPDATED } from './helpGuides';

const TEXT_SIZES = [{ value: 'standard', size: 13 }, { value: 'large', size: 15 }, { value: 'extra-large', size: 18 }];

// spec 200:2796 "v3 / Help – Guide article" at /help/guides/:slug. Content is static (helpGuides.ts); "Was this helpful?" and chat are preview only.
const HelpGuide: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const guide = findGuide(slug);
    const dispatch = useDispatch();
    const user = useSelector((state: IGlobalState) => state.user);
    const [active, setActive] = useState('before');
    const [openFaq, setOpenFaq] = useState(0);

    const sections = guide ? [
        ...(guide.before ? [{ id: 'before', label: 'Before you start' }] : []),
        ...guide.steps.map((s, i) => ({ id: `step-${i + 1}`, label: `Step ${i + 1} · ${s.title}` })),
        ...(guide.faqs.length ? [{ id: 'faq', label: 'Frequently asked questions' }] : []),
    ] : [];

    useEffect(() => {
        window.scrollTo(0, 0);
        setOpenFaq(0);
    }, [slug]);

    // "On this page": the last section whose top has scrolled past the top third of the window
    useEffect(() => {
        const onScroll = () => {
            let current = sections[0]?.id ?? '';
            for (const s of sections) {
                const el = document.getElementById(`hg-${s.id}`);
                if (el && el.getBoundingClientRect().top < window.innerHeight / 3) current = s.id;
            }
            setActive(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    if (!guide) return <div className="v2-page help-guide-v2">
        <div className="hg-crumbs"><Link to="/help">Help centre</Link></div>
        <div className="card hg-missing">
            <h1>We couldn’t find that guide</h1>
            <p>It may have been renamed. Search the help centre, or email {SUPPORT_EMAIL}.</p>
            <Link className="link" to="/help">Back to the help centre <FiArrowRight /></Link>
        </div>
    </div>;

    const jump = (id: string) => {
        setActive(id);
        document.getElementById(`hg-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    // applies straight away, not saved (same as the help panel); My Profile saves it
    const setTextSize = (value: string) => dispatch(SetUser({ ...user, textSize: value }));
    const video = guide.video ? findVideo(guide.video) : undefined;
    const related = guide.related.map(findGuide).filter(g => !!g);

    return <div className="v2-page help-guide-v2">
        <nav className="hg-crumbs" aria-label="Breadcrumb">
            <Link to="/help">Help centre</Link><span>/</span>
            <Link to={topicHref(guide.topic)}>{guide.topic}</Link><span>/</span>
            <strong>{guide.title}</strong>
        </nav>

        <div className="hg-row">
            <aside className="card hg-toc">
                <div className="hg-toc-title">On this page</div>
                {sections.map(s => <button key={s.id} type="button" className={active === s.id ? 'active' : ''} onClick={() => jump(s.id)}>{s.label}</button>)}
                <div className="hg-toc-sizes">
                    <span>Text size</span>
                    <div className="hg-seg">
                        {TEXT_SIZES.map(s => <button key={s.value} type="button" style={{ fontSize: s.size }} aria-label={`Text size ${s.value}`}
                            className={(user.textSize ?? 'standard') === s.value ? 'active' : ''} onClick={() => setTextSize(s.value)}>A</button>)}
                    </div>
                </div>
            </aside>

            <article className="card hg-article">
                <div className="hg-meta">
                    <Link className="hg-badge" to={topicHref(guide.topic)}>{guide.topic}</Link>
                    <span>{guide.minutes} min read</span><span className="dot">·</span><span>Updated {HELP_UPDATED}</span>
                </div>
                <h1>{guide.heading}</h1>
                <p className="hg-intro">{guide.intro}</p>

                {guide.before && <div className="hg-before" id="hg-before">
                    <span className="icon-tile"><FiAlertCircle /></span>
                    <div><strong>Before you start</strong><p>{guide.before}</p></div>
                </div>}

                {guide.steps.map((s, i) => <section key={s.title} className="hg-step" id={`hg-step-${i + 1}`}>
                    <div className="hg-step-head"><span className="hg-num">{i + 1}</span><h2>{s.title}</h2></div>
                    <p>{s.body}</p>
                    {s.link && <Link className="link hg-step-link" to={s.link.to}>{s.link.label} <FiArrowRight /></Link>}
                    {/* no real screenshots yet: the spec's captioned frame */}
                    {s.screenshot && <div className="hg-shot"><span><FiFileText /> {s.screenshot}</span></div>}
                    {s.note && <div className="hg-note"><FiAlertCircle /><span>{s.note}</span></div>}
                </section>)}

                {guide.faqs.length > 0 && <section className="hg-faq" id="hg-faq">
                    <h2>Frequently asked questions</h2>
                    {guide.faqs.map((f, i) => <div key={f.q} className={`hg-faq-item${openFaq === i ? ' open' : ''}`}>
                        <button type="button" aria-expanded={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                            <span>{f.q}</span><FiChevronDown />
                        </button>
                        {openFaq === i && <p>{f.a}</p>}
                    </div>)}
                </section>}

                <div className="hg-feedback">
                    <strong>Was this guide helpful?</strong>
                    <button type="button" className="hg-btn" onClick={() => toast.info('Preview only: thanks! Feedback isn’t recorded yet')}><FiCheck /> Yes, Thanks</button>
                    <button type="button" className="hg-btn" onClick={() => toast.info(`Preview only: feedback isn’t recorded yet — tell us what was missing at ${SUPPORT_EMAIL}`)}>Not Really</button>
                </div>
            </article>

            <div className="hg-rail">
                {video && <div className="card hg-watch">
                    <Link className="hg-thumb" to={videoHref(video.slug)} aria-label={`Watch ${video.title}`}><span className="hg-play"><FiPlay /></span></Link>
                    <strong>Prefer to watch? {video.title}</strong>
                    <small>{video.length} · Video coming soon, transcript available</small>
                    <Link className="link" to={videoHref(video.slug)}>Watch Video <FiArrowRight /></Link>
                </div>}

                {related.length > 0 && <div className="card hg-related">
                    <strong>Related guides</strong>
                    {related.map(g => <Link key={g!.slug} className="hg-related-row" to={guideHref(g!.slug)}>
                        <FiFileText /><span>{g!.title}</span><small>{g!.minutes} min</small>
                    </Link>)}
                </div>}

                <div className="card hg-help">
                    <strong>Still stuck?</strong>
                    <p>Chat with us — usually answered in 2 minutes during business hours.</p>
                    <button type="button" className="hg-chat" onClick={() => toast.info('Preview only: live chat isn’t available yet')}><FiMessageCircle /> Start a Chat</button>
                    <a className="hg-mail" href={`mailto:${SUPPORT_EMAIL}`}>or email {SUPPORT_EMAIL}</a>
                </div>
            </div>
        </div>
    </div>;
};

export default HelpGuide;
